import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Section, showAlert } from '@platform/ui';
import { SEO } from '@platform/seo';
import { buildSeo } from '@/shared/seo/seoBuilder';
import type { BoardType } from '@/shared/constants/app/routes.constant.ts';
import { useBoardDetail } from '../api/use-board.query.ts';
import { useUpdatePost } from '../api/use-board.mutation.ts';
import { useBoardPermissions } from '../hooks/useBoardPermissions.ts';
import { BoardForm } from '../components/BoardForm.tsx';
import { BoardStateView } from '../components/BoardStateView.tsx';
import type { BoardConfig, CreatePostInput, Post } from '../types/board.type.ts';

interface BoardEditViewProps {
  type: BoardType;
  config: BoardConfig<Post>;
}

/**
 * 게시글 수정 컨테이너.
 * 기존 글 로드 → 권한(canUpdate, 소유권 포함) 검사 → 폼 prefill → 수정 mutation.
 */
export function BoardEditView({ type, config }: BoardEditViewProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: post, isLoading, isError, refetch } = useBoardDetail(type, id);
  const perms = useBoardPermissions(type);
  const updateMutation = useUpdatePost(type, id ?? '');

  const handleSubmit = useCallback(
    async (input: CreatePostInput) => {
      const updated = await updateMutation.mutateAsync(input);
      navigate(config.detailPath(updated.id), { replace: true });
    },
    [updateMutation, navigate, config],
  );

  const handleError = useCallback((message: string) => {
    void showAlert({ title: '알림', message, confirmText: '확인' });
  }, []);

  if (isLoading) return <BoardStateView kind="loading" />;
  if (isError || !post) {
    return (
      <BoardStateView kind="error" onRetry={() => refetch()} backTo={config.basePath} />
    );
  }

  // 권한: 수정 권한 또는 본인 글(정책에 따라). 없으면 forbidden.
  if (!perms.canUpdate(post)) {
    return (
      <BoardStateView
        kind="forbidden"
        message="수정 권한이 없습니다."
        backTo={config.detailPath(post.id)}
      />
    );
  }

  const seo = buildSeo({
    title: `${config.title} 수정`,
    description: config.description,
    path: config.editPath(post.id),
  });

  return (
    <>
      <SEO {...seo} />
      <main className="bg-white dark:bg-dark-base text-neutral-950 dark:text-neutral-100 antialiased">
        <Section spacing="default">
          <Container>
            <div className="pt-[100px] sm:pt-28">
              <header className="mb-8 border-b border-black/10 dark:border-white/10 pb-5">
                <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-neutral-950/40 dark:text-white/40">
                  {config.type.toUpperCase()}
                </span>
                <h1 className="mt-2 text-3xl font-extrabold -tracking-[0.03em]">
                  {config.title} 수정
                </h1>
              </header>
              <BoardForm
                type={type}
                config={config}
                initial={post}
                onSubmit={handleSubmit}
                submitting={updateMutation.isPending}
                onCancel={() => navigate(config.detailPath(post.id))}
                onError={handleError}
              />
            </div>
          </Container>
        </Section>
      </main>
    </>
  );
}
